import { useState } from 'react';
import { arrayUtils, textUtils, useToastContext } from '@sato/components';
import { AxiosResponse } from 'axios';

import { Link } from '@components/common/Link';
import { useNavigate } from '@hooks/useNavigate';
import { useLanguage, useTranslate } from '@hooks/useTranslate';
import { BaseIncidentFields } from '@models/api';
import {
  IncidentClassification,
  IncidentSubClassification,
} from '@models/Incident';
import { useDialog } from '@providers/DialogProvider';
import { ROUTES } from '@routes/routes.constants';
import { getMessageTopicRoute } from '@routes/routes.utils';
import { useApartment } from '@stores/apartmentStore';
import { useContractId } from '@stores/contractStore';
import { isLocalOrTestEnv } from '@utils/featureFlags';

export interface UseIncidentCreationProps<T extends BaseIncidentFields> {
  /** Function to post the incident to API */
  postIncident: (value: T) => Promise<AxiosResponse<unknown, unknown>>;
  /** Classifications to be sent with other data */
  classifications?: {
    classification: IncidentClassification;
    subClassification: IncidentSubClassification;
  };
  /** Prefix to add to the message title. Eg. prefix = `"Foo"` and title = `"bar"` would result in `"Foo: bar"` */
  messageTitlePrefix?: string;
  /** Overrides the default body text of the confirmation dialog shown after the message is sent */
  successDialogBody?: string;
}

const hasTitle = <T extends object>(data: T): data is T & { title: string } =>
  'title' in data;

/** Utility hook for sending a new incident */
export const useIncidentCreation = <T extends BaseIncidentFields>({
  postIncident,
  classifications,
  messageTitlePrefix,
  successDialogBody,
}: UseIncidentCreationProps<T>) => {
  const locale = useLanguage();
  const { t } = useTranslate();
  const { openDialog } = useDialog();
  const { addToast } = useToastContext();
  const navigate = useNavigate();

  const { apartment } = useApartment();
  const contractId = useContractId();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const realEstateId = apartment?.realestateID;

  return {
    /** Sends incident message. Handles confirmation modal on success and error toasts on error. */
    create: async (
      data: Omit<
        T,
        | 'contractId'
        | 'realEstateId'
        | 'language'
        | 'classification'
        | 'subClassification'
      >
    ) => {
      try {
        // Should never happen at this point, but check to keep type checker happy
        if (!contractId || !realEstateId) return;

        setIsSubmitting(true);

        // Clone data so that this function doesn't mutate it
        const dataClone = { ...data };

        // Apply title prefix to the message title
        if (messageTitlePrefix && hasTitle(data)) {
          dataClone.title = arrayUtils.joinArrayString(
            [messageTitlePrefix, data.title],
            ': '
          );
        }

        await postIncident({
          ...dataClone,
          ...classifications,
          contractId,
          realEstateId,
          language: textUtils.toUpperCase(locale),
          // Should be safe to cast since we first omit from T, then apply those omitted values
        } as T);

        navigate(ROUTES.MESSAGES);

        // Open confirmation dialog after message is sent
        openDialog({
          heading: t('message_sent_label'),
          body: successDialogBody || t('message_sent_body'),
          icon: 'check',
          hideCloseButton: true,
          layout: 'column',
          linkComponent: Link,
          actionButton: {
            children: t('ok'),
            to: ROUTES.MESSAGES,
          },
          ...(classifications?.classification === 'DEFECT_REPORT'
            ? {
                backButton: {
                  children: t('create_new_defect_report'),
                  to: getMessageTopicRoute({ topicId: 'defect' }),
                },
              }
            : {}),
        });
      } catch (e) {
        addToast({
          message: t('error.HTTP_GENERAL'),
          variant: 'alert',
          duration: 5000,
        });

        if (isLocalOrTestEnv) console.error(e);
      } finally {
        setIsSubmitting(false);
      }
    },
    isSubmitting,
  };
};
